import { Col, Row, Space, Spin, Switch, Typography } from 'antd'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Selector } from 'components/Selector'
import { CIMSTags } from 'components/CIMSTags'
import { useGlobalState } from 'context/GlobalStateContext'
import { errorHandler } from 'hooks/errorHandler'
import { fieldUpdatedNotification } from 'hooks/notificationHandler'
import { useEffect, useState } from 'react'
import {
  useDesignLayerTabPdfDesignLayersQuery,
  useEditPdfDesignLayerMutation,
  useGetProjectMapServerLayersQuery,
} from 'schema'
import { Permission } from 'shared'
import { DesignLayerOption, useDesignLayers } from '../../hooks/useDesignLayers'
import LayerPreview from './components/LayerPreview'
import ProjectLayers from './components/ProjectLayers'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'

type _Map = Map<string, number[] | undefined>

const DesignLayerTab: React.VFC<DesignLayerOption> = (option) => {
  // state pieces + hooks
  const canEdit = useCheckPermission(Permission.EditPDFDesignLayer)
  const [globalIds, setGlobalIds] = useState<string[]>()
  const [globalId, setGlobalId] = useState<string>()
  const {
    placeholder,
    selectOptionMessage,
    reports,
    loading: layerLoading,
    options: designLayerOptions,
  } = useDesignLayers(option)
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const {
    data,
    loading: getLoading,
    refetch,
  } = useDesignLayerTabPdfDesignLayersQuery({
    skip: !projectGlobalId,
    variables: { projectId: projectGlobalId! },
    onError: errorHandler,
    fetchPolicy: 'no-cache',
  })
  const {
    data: mapServerLayersData,
    loading: layersLoading,
    refetch: layersRefetch,
  } = useGetProjectMapServerLayersQuery({
    ...DefaultQueryHookOptions,
    skip: !projectGlobalId,
    variables: { globalId: projectGlobalId! },
  })
  const projectFeatureLayers =
    mapServerLayersData?.project?.projectMetadata?.mapServerLayers
  // reset necessary state on change
  useEffect(() => {
    setGlobalId(undefined)
    setGlobalIds([])
    setInitialLayers([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option])

  // loading
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(getLoading || layerLoading || layersLoading)
    return () => setLoading(false)
  }, [getLoading, layerLoading, layersLoading])

  // mutation pieces
  const [edit] = useEditPdfDesignLayerMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => fieldUpdatedNotification(option.name),
  })
  const [globalIdsToLayers, setGlobalIdsToLayers] = useState<_Map>(new Map())
  const [allLayers, setAllLayers] = useState(Array<number>())
  const [useAllLayers, setUseAllLayers] = useState(false)
  useEffect(() => {
    const json = data?.project.pdfDesignLayers.find(
      ({ index: i }) => i === option?.featureServerId
    )?.json
    setAllLayers(json?.allLayers ?? [])
    const map: _Map = new Map()
    Object.keys(json?.options ?? {}).forEach((key) =>
      map.set(key, json?.options[key])
    )
    setGlobalIdsToLayers(map)
    setUseAllLayers(json?.useAllLayers ?? false)
    return () => {
      setAllLayers([])
      setGlobalIdsToLayers(new Map())
      setUseAllLayers(false)
    }
  }, [data?.project.pdfDesignLayers, option?.featureServerId, option])

  // initial layers
  const [initialLayers, setInitialLayers] = useState<number[] | undefined>()
  useEffect(() => {
    if (useAllLayers) return setInitialLayers(allLayers)
    let layers = new Set<number>()
    if (canEdit) {
      if (!globalIds?.length) return setInitialLayers(undefined)
      globalIds.forEach((globalId) => {
        const _layers = globalIdsToLayers.get(globalId) ?? []
        _layers.forEach((layer) => layers.add(layer))
      })
    } else {
      if (!globalId) return setInitialLayers(undefined)
      layers = new Set(globalIdsToLayers.get(globalId) ?? [])
    }
    setInitialLayers([...layers.values()])
    return () => setInitialLayers([])
  }, [allLayers, globalIds, globalIdsToLayers, useAllLayers, globalId, canEdit])
  // refetch layers
  useEffect(() => {
    layersRefetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option])

  return (
    <Row>
      <Col flex="48.5%">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <CIMSTags
            header={{ content: 'Applicable Reports' }}
            tags={reports.map(({ name }) => ({ text: name }))}
          />
          <Selector
            multiple={canEdit}
            placeholder={placeholder}
            options={designLayerOptions}
            currentValue={canEdit ? globalIds : globalId}
            onChange={(change) => {
              canEdit
                ? setGlobalIds(change as string[])
                : setGlobalId(change as string)
            }}
            style={{ width: '100%' }}
            loading={loading}
            disabled={loading || useAllLayers}
          />
          <Row justify="space-between">
            <Typography.Text>Use with all {option?.name}</Typography.Text>
            <Switch
              disabled={loading || !canEdit}
              checked={useAllLayers}
              onChange={() => {
                setUseAllLayers((prev) => !prev)
                setGlobalIds(undefined)
                setGlobalId(undefined)
              }}
            />
          </Row>
          {(!globalIds?.length || !globalId) && !useAllLayers && (
            <Typography.Text>{selectOptionMessage()}</Typography.Text>
          )}
          {(!!globalIds?.length || !!globalId || useAllLayers) && (
            <>
              {canEdit ? (
                <ProjectLayers
                  loading={loading}
                  initialLayers={initialLayers}
                  onChange={(layers) => {
                    setInitialLayers(layers)
                    if (useAllLayers) setAllLayers(layers)
                  }}
                  onSave={async () => {
                    if (!projectGlobalId)
                      return errorHandler('projectId is not defined')
                    const _map = new Map(globalIdsToLayers)
                    // this should work, but if stuff breaks. this is the first place to look
                    globalIds?.forEach((globalId) =>
                      _map.set(globalId, initialLayers)
                    )
                    const options = [..._map.entries()].reduce<
                      Record<string, number[]>
                    >((prev, [key, value]) => {
                      if (value) prev[key] = value
                      return prev
                    }, {})
                    setLoading(true)
                    await edit({
                      variables: {
                        input: {
                          index: option?.featureServerId,
                          projectId: projectGlobalId,
                          json: {
                            useAllLayers,
                            allLayers,
                            options: useAllLayers ? {} : options,
                          },
                        },
                      },
                    })
                    await refetch()
                    setLoading(false)
                  }}
                />
              ) : (
                <Spin spinning={loading}>
                  <Space align="start" size="large" wrap>
                    {!!initialLayers?.length ? (
                      <>
                        <Typography.Text strong>
                          Configured project feature layers
                        </Typography.Text>
                        <CIMSTags
                          tags={projectFeatureLayers
                            ?.filter(({ id }) => initialLayers.includes(id))
                            .map(({ name: text }) => ({ text }))}
                        />
                      </>
                    ) : (
                      <Typography.Text>
                        There are no project feature layers configured for the
                        selected {option.name}
                      </Typography.Text>
                    )}
                  </Space>
                </Spin>
              )}
            </>
          )}
        </Space>
      </Col>
      <Col flex="auto" />
      <Col flex="48.5%">
        <LayerPreview option={option} currentLayers={initialLayers} />
      </Col>
    </Row>
  )
}

export default DesignLayerTab
